import React, { useEffect, useState } from 'react'
import { MarkUserNotificationRead$Request, NotificationType, UserNotificationDto } from '../../../../services/api.types'
import { t } from 'ttag'
import YumButton from '../../../../components/YumButton/YumButton'
import { Box } from '@material-ui/core'
import Modal from '@material-ui/core/Modal'
import Backdrop from '@material-ui/core/Backdrop'
import Fade from '@material-ui/core/Fade'

import { ReactComponent as NotificationIcon } from '../../../../assets/v3/icons/notifications/notification.svg'
import { ReactComponent as NotificationUnreadIcon } from '../../../../assets/v3/icons/notifications/notification-unread.svg'
import { ReactComponent as DropdownIcon } from '../../../../assets/v3/icons/notifications/dropdown.svg'
import { ReactComponent as CloseIcon } from '../../../../assets/v3/icons/close.svg'
import { ReactComponent as DownloadIcon } from '../../../../assets/v3/icons/ranking/download.svg'

import { darkBlue, darkGray, gray, primaryBlue, primaryLightBlue } from '../../../../styles/material-ui-theme-v2'
import dayjs from '../../../../dayjsConfig'
import { currentLocale } from '../../../../i18nInit'
import { isNative } from '../../../../common/helpers/isNative'
import { openUrl } from '../../../../common/helpers/routeHelpers'
import { store } from '../../../../index'
import { push } from 'connected-react-router'
import { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import Loader from '../../../../components/Loader/Loader'
import { download, downloadImage } from '../../../../common/helpers/downloadHelpers'
import { formatDescription } from '../../../../common/helpers/activitiesHelpers'

function getFormattedTime(dateNumber: number): string {
  if (!dateNumber) return ''
  const date = new Date(dateNumber)
  const now = dayjs()

  // Check if the date is less than a day ago
  if (now.diff(date, 'days') < 1 && dayjs(date).isBefore(dayjs())) {
    // Use Moment.js relative time with short units
    return dayjs(date).fromNow()
  }

  return now.year() === dayjs(date).year()
    ? date.toLocaleDateString(currentLocale, {
        //year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : date.toLocaleDateString(currentLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
}

export interface IUserNotification {
  notification: UserNotificationDto
  isOpen?: boolean
  isNotification?: boolean
}

export interface IUserNotificationDispatchProps {
  markUserNotificationRead(payload: MarkUserNotificationRead$Request): void
}

export default function UserNotification(props: IUserNotification & IUserNotificationDispatchProps) {
  const { notification, markUserNotificationRead, isOpen: isOpenProp, isNotification } = props
  const {
    id,
    notificationId,
    type,
    read,
    title,
    message,
    sentAt,
    senderName,
    companyName,
    ctaUrl,
    ctaText,
    imageThumbUrl,
    imageFullUrl,
  } = notification
  const isAnnouncement = type === NotificationType.ANNOUNCEMENT

  const [isOpen, setIsOpen] = useState(isOpenProp)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [isThumbImgLoading, setIsThumbImgLoading] = useState(true)
  const [isFullImgLoading, setIsFullImgLoading] = useState(true)

  const handleThumbImageLoad = () => {
    setTimeout(() => {
      setIsThumbImgLoading(false)
    }, 10)
  }

  const handleThumbImageError = () => {
    setTimeout(() => {
      setIsThumbImgLoading(false)
    }, 10)
  }

  const handleFullImageLoad = () => {
    setTimeout(() => {
      setIsFullImgLoading(false)
    }, 10)
  }

  const handleFullImageError = () => {
    setTimeout(() => {
      setIsFullImgLoading(false)
    }, 10)
  }

  useEffect(() => {
    setIsThumbImgLoading(!!isOpen)
    setIsFullImgLoading(!!isOpen)
  }, [isOpen, imageFullUrl])

  useEffect(() => {
    if (isOpenProp && !read) {
      markUserNotificationRead({ userNotificationId: id })
    }
    setIsOpen(isOpenProp)
  }, [isOpenProp, read])

  const handleClick = () => {
    if (!isOpen) {
      if (!read) markUserNotificationRead({ userNotificationId: id })
      if (id) {
        store.dispatch(push(`/inbox/${notificationId}`))
      } else {
        setIsOpen(!isOpen)
      }
    }
  }

  const isInternal =
    ctaUrl &&
    (ctaUrl.includes('localhost:3000') || ctaUrl.includes('staging.yumuuv.com') || ctaUrl.includes('app.yumuuv.com'))

  const makeCtaUrlInternal = (url: string) => {
    const internalUrl = 'yumuuv:/'
    return url
      .replace('http://localhost:3000', internalUrl)
      .replace('https://staging.yumuuv.com', internalUrl)
      .replace('https://app.yumuuv.com', internalUrl)
  }

  const handleImageClick = () => {
    setIsModalOpen(true)
  }

  const handleClose = () => {
    if (id) {
      store.dispatch(push(`/inbox/${isNotification ? 'notifications' : 'announcements'}`))
    } else {
      setIsOpen(!isOpen)
    }
  }

  const handleDownload = (e: any) => {
    if (imageFullUrl) {
      e.preventDefault()
      downloadImage(imageFullUrl)
    }
  }

  return (
    <div
      id={`n-${notificationId}`}
      onClick={handleClick}
      style={{
        marginBottom: '16px',
        padding: '10px',
        borderRadius: '8px',
        border: `1px solid ${isOpen ? primaryBlue : read ? darkGray : 'transparent'}`,
        backgroundColor: read || isOpen ? 'white' : primaryLightBlue,
        cursor: isOpen ? 'inherit' : 'pointer',
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <div style={{ display: 'flex', gap: '10px', flexDirection: 'column', wordBreak: 'break-word', width: '100%' }}>
          <div style={{ display: 'flex', gap: '9px', alignItems: 'center' }}>
            {read || isOpen ? <NotificationIcon /> : <NotificationUnreadIcon />}
            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{title}</div>
          </div>

          <div
            style={{
              fontSize: '12px',
              fontWeight: '500',
              color: darkBlue,
              opacity: '0.7',
            }}
          >
            {isAnnouncement ? (
              <>
                {companyName}
                {sentAt ? <span> &middot; {getFormattedTime(sentAt)}</span> : ''}
              </>
            ) : (
              <>{sentAt ? getFormattedTime(sentAt) : ''}</>
            )}
          </div>

          {isOpen && (
            <>
              <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{formatDescription(message)}</div>
              {ctaText && ctaUrl ? (
                <div>
                  <Box sx={{ display: { xs: 'block', sm: 'inline-block' } }}>
                    {isNative() && !isInternal ? (
                      <YumButton size="small" style={{ display: 'block' }} onClick={() => openUrl(ctaUrl, true)}>
                        {ctaText}
                      </YumButton>
                    ) : (
                      <YumButton
                        size="small"
                        href={isNative() && isInternal ? makeCtaUrlInternal(ctaUrl) : ctaUrl}
                        target={isInternal ? '_self' : '_blank'}
                        style={{ display: 'block' }}
                      >
                        {ctaText}
                      </YumButton>
                    )}
                  </Box>
                </div>
              ) : (
                ''
              )}
              {imageThumbUrl && imageFullUrl ? (
                <>
                  <Modal
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    closeAfterTransition
                    BackdropComponent={Backdrop}
                    BackdropProps={{
                      timeout: 500,
                      /*style: { cursor: 'zoom-out' },*/
                    }}
                    //onBackdropClick={() => setIsModalOpen(false)}
                  >
                    <Fade in={isModalOpen}>
                      <div
                        onClick={() => setIsModalOpen(false)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          justifyContent: 'center',
                          height: '100%',
                          outline: 'none',
                          //cursor: 'zoom-out',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '25px',
                            right: '25px',
                            display: 'flex',
                            // @ts-ignore
                            gap: '20px',
                            alignItems: 'center',

                            /* iOS Safari 11.2, Safari 11 */
                            '@supports (bottom: constant(safe-area-inset-bottom))': {
                              top: 'calc(constant(safe-area-inset-top) + 25px)',
                            },
                            /* iOS Safari 11.4+, Safari 11.1+, Chrome 69+, Opera 56+ */
                            '@supports (bottom: env(safe-area-inset-bottom))': {
                              top: 'calc(env(safe-area-inset-top) + 25px)',
                            },
                          }}
                        >
                          <a
                            href={imageFullUrl}
                            target="_blank"
                            style={{ padding: '4px', color: darkBlue, lineHeight: 1 }}
                            rel="noreferrer"
                            download
                            onClick={handleDownload}
                          >
                            <DownloadIcon />
                          </a>
                          <CloseIcon
                            style={{
                              /* @ts-ignore */
                              cursor: 'pointer',
                            }}
                            onClick={() => setIsModalOpen(false)}
                          />
                        </Box>
                        <img
                          src={imageFullUrl}
                          alt={title}
                          style={{
                            display: 'block',
                            padding: '60px 0',
                            maxWidth: '100%',
                            maxHeight: '100%',
                            height: 'auto',
                            width: 'auto',
                            position: 'relative',
                            zIndex: 2,
                          }}
                          onLoad={handleFullImageLoad}
                          onError={handleFullImageError}
                        />
                        {isFullImgLoading && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              marginLeft: '-12px',
                              marginTop: '-12px',
                            }}
                          >
                            <Loader noPadding={true} />
                          </div>
                        )}
                      </div>
                    </Fade>
                  </Modal>
                  <div
                    onClick={handleImageClick}
                    style={{
                      cursor: 'zoom-in',
                      padding: '8px',
                      borderRadius: '8px',
                      backgroundColor: gray,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        alignSelf: 'stretch',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '9px',
                      }}
                    >
                      <div
                        style={{
                          position: 'relative',
                          backgroundColor: 'white',
                          width: '48px',
                          height: '48px',
                          borderRadius: '6px',
                          overflow: 'hidden',
                        }}
                      >
                        <img
                          src={imageThumbUrl}
                          alt={title}
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '6px',
                            objectFit: 'cover',
                            position: 'relative',
                            zIndex: 2,
                          }}
                          onLoad={handleThumbImageLoad}
                          onError={handleThumbImageError}
                        />
                        {isThumbImgLoading && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              marginLeft: '-12px',
                              marginTop: '-12px',
                            }}
                          >
                            <Loader noPadding={true} />
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          flex: '1',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                          gap: '4px',
                        }}
                      >
                        <div style={{ fontSize: '12px', fontWeight: '500' }}>{t`Attachment`}</div>
                        <div style={{ fontSize: '14px' }}>{t`Tap here to view image`}</div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                ''
              )}

              {isAnnouncement && (
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: darkBlue,
                    opacity: '0.7',
                  }}
                >
                  {t`Sender`}: {senderName}
                </div>
              )}
            </>
          )}

          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <DropdownIcon
              style={{
                //transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                transition: 'transform 150ms',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
            <Box
              onClick={handleClose}
              sx={{
                /* @ts-ignore */
                cursor: 'pointer',
                '@media (hover:hover)': {
                  '&:hover': { textDecoration: 'underline' },
                },
                '&:active': { textDecoration: 'underline' },
              }}
            >
              {isAnnouncement
                ? isOpen
                  ? t`Close the announcement`
                  : t`View the announcement`
                : isOpen
                  ? t`Close the notification`
                  : t`View the notification`}
            </Box>
          </div>
        </div>
      </div>
    </div>
  )
}
